import type { AiUsageInput } from "./types.js";

/** Model id field priority (higher wins). Runtime ids beat request/config aliases. */
export const MODEL_FIELD_PRIORITY: readonly { key: string; score: number }[] = [
  { key: "usedModel", score: 1000 },
  { key: "modelUsed", score: 990 },
  { key: "resolvedModel", score: 980 },
  { key: "model", score: 500 },
  { key: "xynthesisModel", score: 120 },
  { key: "skillModel", score: 110 },
] as const;

/** Path segments that boost or penalize a model candidate. */
const MODEL_PATH_BONUS: readonly { pattern: RegExp; bonus: number }[] = [
  { pattern: /(^|\.)response(\.|$)/i, bonus: 40 },
  { pattern: /(^|\.)outer\.input(\.|$)/i, bonus: 35 },
  { pattern: /(^|\.)config(?!\.rawConfig)(\.|$)/i, bonus: 30 },
  { pattern: /(^|\.)outer\.input\.config(\.|$)/i, bonus: 28 },
  { pattern: /(^|\.)usage(\.|$)/i, bonus: 15 },
  { pattern: /(^|\.)rawConfig(\.|$)/i, bonus: -40 },
  { pattern: /workingMemory/i, bonus: -50 },
  { pattern: /requestExample/i, bonus: -80 },
  { pattern: /graphsStudio/i, bonus: -80 },
];

const PROVIDER_FIELD_PRIORITY: readonly { key: string; score: number }[] = [
  { key: "provider", score: 500 },
  { key: "providerId", score: 480 },
] as const;

const PROVIDER_PATH_BONUS: readonly { pattern: RegExp; bonus: number }[] = [
  { pattern: /(^|\.)config(?!\.rawConfig)(\.|$)/i, bonus: 30 },
  { pattern: /(^|\.)outer\.input(\.|$)/i, bonus: 20 },
  { pattern: /(^|\.)rawConfig(\.|$)/i, bonus: -20 },
];

const PROMPT_TOKEN_KEYS = new Set([
  "prompt",
  "prompt_tokens",
  "promptTokens",
  "input_tokens",
  "inputTokens",
  "estimatedInputTokens",
  "input",
]);

const COMPLETION_TOKEN_KEYS = new Set([
  "completion",
  "completion_tokens",
  "completionTokens",
  "output_tokens",
  "outputTokens",
  "estimatedOutputTokens",
  "output",
]);

const OPTIONAL_TOKEN_KEYS: Record<string, keyof AiUsageInput["tokens"]> = {
  total: "total",
  total_tokens: "total",
  totalTokens: "total",
  cached: "cached",
  cached_tokens: "cached",
  cache_read_tokens: "cached",
  cacheReadTokens: "cached",
  cacheWrite: "cacheWrite",
  cache_write_tokens: "cacheWrite",
  cacheWriteTokens: "cacheWrite",
  reasoning: "reasoning",
  reasoning_tokens: "reasoning",
  reasoningTokens: "reasoning",
  audio: "audio",
  audio_tokens: "audio",
  image: "image",
  image_tokens: "image",
};

export type FieldProvenance = {
  field: string;
  path: string;
  score: number;
};

export type TokenExtractionProvenance = {
  source: "usage" | "tokens" | "diagnostics" | "estimated";
  path: string;
  estimated?: boolean;
};

export type ExtractUsageInputResult = {
  input: AiUsageInput;
  provenance: {
    model: FieldProvenance;
    modelUsed?: FieldProvenance;
    provider?: FieldProvenance;
    tokens: TokenExtractionProvenance;
  };
};

export type ExtractUsageInputOptions = {
  /** Default when no provider field is found (default: `openrouter`). */
  defaultProvider?: string;
};

type ModelCandidate = FieldProvenance & { value: string };
type ProviderCandidate = FieldProvenance & { value: string };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function modelKeyScore(key: string): number {
  return MODEL_FIELD_PRIORITY.find((e) => e.key === key)?.score ?? 0;
}

function providerKeyScore(key: string): number {
  return PROVIDER_FIELD_PRIORITY.find((e) => e.key === key)?.score ?? 0;
}

function pathBonus(path: string, rules: readonly { pattern: RegExp; bonus: number }[]): number {
  let bonus = 0;
  for (const { pattern, bonus: b } of rules) {
    if (pattern.test(path)) bonus += b;
  }
  return bonus;
}

function normalizeModelValue(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

function normalizeProviderValue(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim().toLowerCase();
  if (!t || t === "null") return undefined;
  return t;
}

function collectModelCandidates(
  node: unknown,
  path: string,
  out: ModelCandidate[],
  depth: number,
  maxDepth: number,
): void {
  if (depth > maxDepth || node === null || node === undefined) return;

  if (isPlainObject(node)) {
    for (const [key, value] of Object.entries(node)) {
      const childPath = path ? `${path}.${key}` : key;
      const base = modelKeyScore(key);
      if (base > 0) {
        const model = normalizeModelValue(value);
        if (model) {
          out.push({
            field: key,
            path: childPath,
            value: model,
            score: base + pathBonus(childPath, MODEL_PATH_BONUS),
          });
        }
      }
      collectModelCandidates(value, childPath, out, depth + 1, maxDepth);
    }
    return;
  }

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      collectModelCandidates(node[i], `${path}[${i}]`, out, depth + 1, maxDepth);
    }
  }
}

function collectProviderCandidates(
  node: unknown,
  path: string,
  out: ProviderCandidate[],
  depth: number,
  maxDepth: number,
): void {
  if (depth > maxDepth || node === null || node === undefined) return;

  if (isPlainObject(node)) {
    for (const [key, value] of Object.entries(node)) {
      const childPath = path ? `${path}.${key}` : key;
      const base = providerKeyScore(key);
      if (base > 0) {
        const provider = normalizeProviderValue(value);
        if (provider) {
          out.push({
            field: key,
            path: childPath,
            value: provider,
            score: base + pathBonus(childPath, PROVIDER_PATH_BONUS),
          });
        }
      }
      collectProviderCandidates(value, childPath, out, depth + 1, maxDepth);
    }
    return;
  }

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      collectProviderCandidates(node[i], `${path}[${i}]`, out, depth + 1, maxDepth);
    }
  }
}

function readNumber(obj: Record<string, unknown>, keys: Set<string>): number | undefined {
  for (const [k, v] of Object.entries(obj)) {
    if (!keys.has(k)) continue;
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
  }
  return undefined;
}

function readOptionalNumbers(
  obj: Record<string, unknown>,
): Partial<AiUsageInput["tokens"]> {
  const extra: Partial<AiUsageInput["tokens"]> = {};
  for (const [k, v] of Object.entries(obj)) {
    const target = OPTIONAL_TOKEN_KEYS[k];
    if (!target || typeof v !== "number" || !Number.isFinite(v) || v < 0) continue;
    extra[target] = v;
  }
  return extra;
}

function looksLikeUsageObject(obj: Record<string, unknown>): boolean {
  const hasPrompt = readNumber(obj, PROMPT_TOKEN_KEYS) !== undefined;
  const hasCompletion = readNumber(obj, COMPLETION_TOKEN_KEYS) !== undefined;
  return hasPrompt && hasCompletion;
}

function looksLikeDiagnosticsObject(obj: Record<string, unknown>): boolean {
  return (
    typeof obj.estimatedInputTokens === "number" &&
    typeof obj.estimatedOutputTokens === "number"
  );
}

type TokenCandidate = {
  tokens: AiUsageInput["tokens"];
  provenance: TokenExtractionProvenance;
  score: number;
};

function tokensFromObject(
  obj: Record<string, unknown>,
  source: TokenExtractionProvenance["source"],
  path: string,
  estimated: boolean,
  score: number,
): TokenCandidate | null {
  const prompt = readNumber(obj, PROMPT_TOKEN_KEYS);
  const completion = readNumber(obj, COMPLETION_TOKEN_KEYS);
  if (prompt === undefined || completion === undefined) return null;

  const extra = readOptionalNumbers(obj);
  const total =
    typeof extra.total === "number" ? extra.total : prompt + completion;

  return {
    tokens: {
      prompt,
      completion,
      total,
      ...extra,
    },
    provenance: { source, path, estimated },
    score,
  };
}

function collectTokenCandidates(
  node: unknown,
  path: string,
  out: TokenCandidate[],
  depth: number,
  maxDepth: number,
): void {
  if (depth > maxDepth || node === null || node === undefined) return;

  if (isPlainObject(node)) {
    if (path.endsWith("diagnostics") || looksLikeDiagnosticsObject(node)) {
      const cand = tokensFromObject(node, "diagnostics", path, true, 200);
      if (cand) out.push(cand);
    } else if (path.endsWith("usage") || path.endsWith("tokens") || looksLikeUsageObject(node)) {
      const source = path.endsWith("tokens") ? "tokens" : "usage";
      const estimated = false;
      const score = path.endsWith("usage") ? 500 : 450;
      const cand = tokensFromObject(node, source, path, estimated, score);
      if (cand) out.push(cand);
    }

    for (const [key, value] of Object.entries(node)) {
      const childPath = path ? `${path}.${key}` : key;
      if (key === "usage" || key === "tokens" || key === "diagnostics") {
        if (isPlainObject(value)) {
          collectTokenCandidates(value, childPath, out, depth + 1, maxDepth);
        }
      } else {
        collectTokenCandidates(value, childPath, out, depth + 1, maxDepth);
      }
    }
    return;
  }

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      collectTokenCandidates(node[i], `${path}[${i}]`, out, depth + 1, maxDepth);
    }
  }
}

function pickBestModel(candidates: ModelCandidate[]): ModelCandidate | null {
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => b.score - a.score)[0]!;
}

function pickBestProvider(candidates: ProviderCandidate[]): ProviderCandidate | null {
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => b.score - a.score)[0]!;
}

function pickBestTokens(candidates: TokenCandidate[]): TokenCandidate | null {
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => b.score - a.score)[0]!;
}

function inferProviderFromModel(model: string, fallback: string): string {
  const slash = model.indexOf("/");
  if (slash > 0) {
    const prefix = model.slice(0, slash).toLowerCase();
    if (prefix !== "openrouter") return prefix;
  }
  return fallback;
}

/**
 * Extract {@link AiUsageInput} from activity logs, gateway records, MongoDB exports,
 * or any nested JSON shape (e.g. `outer.input`, `config`, `metadata.diagnostics`).
 */
export function extractUsageInput(
  record: unknown,
  options: ExtractUsageInputOptions = {},
): ExtractUsageInputResult {
  const defaultProvider = options.defaultProvider ?? "openrouter";
  const maxDepth = 24;

  const modelCandidates: ModelCandidate[] = [];
  const providerCandidates: ProviderCandidate[] = [];
  const tokenCandidates: TokenCandidate[] = [];

  collectModelCandidates(record, "", modelCandidates, 0, maxDepth);
  collectProviderCandidates(record, "", providerCandidates, 0, maxDepth);
  collectTokenCandidates(record, "", tokenCandidates, 0, maxDepth);

  const bestModel = pickBestModel(modelCandidates);
  if (!bestModel) {
    throw new Error(
      "[ai-tools] Could not extract a model from the record (looked for usedModel, modelUsed, model, …).",
    );
  }

  const runtimeFields = new Set(["usedModel", "modelUsed", "resolvedModel"]);
  const isRuntimeModel = runtimeFields.has(bestModel.field);
  const requestModel = modelCandidates.find(
    (c) => c.field === "model" && c.path !== bestModel.path,
  );

  const bestProvider = pickBestProvider(providerCandidates);
  const provider =
    bestProvider?.value ??
    inferProviderFromModel(bestModel.value, defaultProvider);

  const bestTokens = pickBestTokens(tokenCandidates);
  if (!bestTokens) {
    throw new Error(
      "[ai-tools] Could not extract token usage from the record (usage, tokens, or metadata.diagnostics).",
    );
  }

  const input: AiUsageInput = {
    tokens: bestTokens.tokens,
    provider,
    ...(isRuntimeModel
      ? { modelUsed: bestModel.value, model: requestModel?.value }
      : { model: bestModel.value }),
  };

  const provenance: ExtractUsageInputResult["provenance"] = {
    model: {
      field: bestModel.field,
      path: bestModel.path,
      score: bestModel.score,
    },
    tokens: bestTokens.provenance,
  };

  if (bestProvider) {
    provenance.provider = {
      field: bestProvider.field,
      path: bestProvider.path,
      score: bestProvider.score,
    };
  }

  if (isRuntimeModel && requestModel) {
    provenance.modelUsed = {
      field: bestModel.field,
      path: bestModel.path,
      score: bestModel.score,
    };
  } else if (input.modelUsed) {
    provenance.modelUsed = provenance.model;
  }

  return { input, provenance };
}
